// frontend/src/components/onboarding/OnboardingBanner.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useCompanionName } from '@/hooks/useCompanionName';
import type { FamilyOnboarding } from '@/types/api';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { COARSE_HIT_16 } from '@/lib/touch-target';
import { MonoLabel } from '@/components/editorial';
import { onboardingProgress, onboardingWorkingSteps, type WorkingStep } from './steps';

interface Props {
  onboarding: FamilyOnboarding;
  /** Called with the wizard step to reopen at: the first open step, the one
   *  the banner's "next" sentence names (#2385). */
  onContinue: (step: WorkingStep) => void;
  onDismiss: () => void;
  mfaEnabled?: boolean;
  /** Optional override. When omitted the banner derives this from
   *  `onboarding.steps.voice_chosen` — which is what the wizard's Step 5
   *  actually writes. */
  voiceConfigured?: boolean;
}

// #2386: the same five working steps the wizard's header counts (steps.ts).

export function OnboardingBanner({
  onboarding,
  onContinue,
  onDismiss,
  mfaEnabled = false,
  voiceConfigured,
}: Props) {
  const companionName = useCompanionName();
  const { t } = useTranslation(['onboarding', 'common']);
  const [dismissing, setDismissing] = useState(false);

  if (onboarding.completed || onboarding.banner_dismissed) return null;

  const progress = onboardingProgress(onboarding, { mfaEnabled, voiceConfigured });
  const TOTAL_STEPS = onboardingWorkingSteps();
  const stepsComplete = progress.done;

  if (stepsComplete === TOTAL_STEPS) return null;

  async function handleDismiss() {
    setDismissing(true);
    try {
      await api.families.updateOnboarding({ banner_dismissed: true });
      onDismiss();
    } catch {
      // On failure, re-enable the button — don't dismiss
    } finally {
      setDismissing(false);
    }
  }

  // Next-step hint — the first open step, the one Continue reopens the wizard
  // at. One whole sentence per next step (#2109 R3 L1), never
  // "…next, {fragment}."
  const nextStep = progress.next ?? 'almost';
  const handleContinue = () => onContinue(progress.nextStep ?? 1);

  const progressPct = Math.round((stepsComplete / TOTAL_STEPS) * 100);

  return (
    <div className="isolate rounded-lg border border-stevens-indigo/20 bg-stevens-indigo/5 px-4 py-3 mb-4 flex items-center gap-4">
      <button
        type="button"
        onClick={handleContinue}
        className="flex-1 min-w-0 text-left"
      >
        <MonoLabel tone="indigo" className="block text-stevens-indigo/85 mb-0.5">
          {t('banner.eyebrow')}
        </MonoLabel>
        <p
          className="font-serif text-lede font-medium leading-snug text-stevens-indigo"
        >
          {t('banner.title', { companion: companionName })}
        </p>
        <p
          className="font-serif text-body-sm italic text-stevens-ink-3 leading-relaxed mt-0.5"
        >
          {t(`banner.progress.${nextStep}`, { done: stepsComplete, total: TOTAL_STEPS, companion: companionName })}
        </p>
      </button>

      {/* Step pips — 5 dots that fill as the family progresses */}
      <div
        className="hidden sm:flex lg:hidden xl:flex items-center gap-1.5 shrink-0"
        aria-label={t('banner.percentComplete', { percent: progressPct })}
      >
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-[3px] w-6 rounded-full transition-colors',
              i < stepsComplete ? 'bg-stevens-indigo' : 'bg-stevens-indigo/20',
            )}
          />
        ))}
      </div>

      <Button
        size="sm"
        variant="brand" className="shrink-0"
        onClick={handleContinue}
      >
        {t('common:actions.continue')}
      </Button>
      <button
        type="button"
        aria-label={t('banner.dismiss')}
        // #2603 (D-2420): the 16 px X reaches 44 px on a coarse pointer. The
        // -z-10 hit area sits under Continue, an in-flow box inside this `isolate` row.
        className={cn(
          'text-stevens-ink-3 hover:text-foreground transition-colors shrink-0 disabled:opacity-50',
          COARSE_HIT_16,
        )}
        disabled={dismissing}
        onClick={handleDismiss}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
